import React, { useState } from 'react';
import {
  Box,
  Button,
  Container,
  Grid,
  Paper,
  TextField,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import EmailIcon from '@material-ui/icons/Email';
import BugReportIcon from '@material-ui/icons/BugReport';
import EmojiObjectsIcon from '@material-ui/icons/EmojiObjects';

const useStyles = makeStyles((theme) => ({
  root: {
    marginTop: theme.spacing(3),
  },
  banner: {
    borderRadius: 26,
    border: '1px solid rgba(166, 140, 118, 0.28)',
    background: 'linear-gradient(118deg, #fff8ef 0%, #f3e7d9 60%, #ebdac9 130%)',
    boxShadow: '0 16px 32px rgba(63, 46, 31, 0.1)',
    padding: theme.spacing(3),
    marginBottom: theme.spacing(2),
  },
  heading: {
    fontFamily: '"Manrope", sans-serif',
    fontWeight: 800,
    letterSpacing: '-0.02em',
    color: '#2f261f',
  },
  subheading: {
    color: '#645649',
    maxWidth: 680,
    marginTop: theme.spacing(0.8),
  },
  card: {
    borderRadius: 18,
    border: '1px solid rgba(169, 144, 124, 0.28)',
    background: 'linear-gradient(180deg, #fffdf9 0%, #f9f1e7 100%)',
    padding: theme.spacing(2),
    height: '100%',
  },
  formPanel: {
    borderRadius: 20,
    border: '1px solid rgba(169, 144, 124, 0.3)',
    background: 'linear-gradient(180deg, #fffefb 0%, #f8efe3 100%)',
    padding: theme.spacing(2.2),
  },
  success: {
    borderRadius: 12,
    border: '1px solid rgba(112, 148, 121, 0.36)',
    background: '#f2f8f2',
    padding: theme.spacing(1, 1.2),
    marginBottom: theme.spacing(1.2),
  },
}));

const initialForm = {
  name: '',
  email: '',
  message: '',
};

const validate = (form) => {
  const errors = {};
  if (!form.name.trim()) errors.name = 'Name is required';
  if (!form.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) errors.email = 'Valid email is required';
  if (form.message.trim().length < 20) errors.message = 'Message should be at least 20 characters';
  return errors;
};

const contactCards = [
  {
    icon: <BugReportIcon color="secondary" />,
    title: 'Bug reports',
    text: 'Found an issue? Share steps and screenshots so we can fix it fast.',
  },
  {
    icon: <EmojiObjectsIcon color="secondary" />,
    title: 'Feature ideas',
    text: 'Tell us what would make your planning workflow better.',
  },
  {
    icon: <EmailIcon color="secondary" />,
    title: 'General inquiries',
    text: 'Partnerships, feedback, or questions are always welcome.',
  },
];

const ContactPage = () => {
  const classes = useStyles();
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);

  const onSubmit = (event) => {
    event.preventDefault();
    const nextErrors = validate(form);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;
    setSubmitted(true);
    setForm(initialForm);
  };

  return (
    <Container maxWidth="xl" className={classes.root}>
      <Paper elevation={0} className={classes.banner}>
        <Typography variant="h4" className={classes.heading}>Contact Your Travel Guide</Typography>
        <Typography variant="body1" className={classes.subheading}>
          Share bug reports, feature requests, and product feedback. We read every message and use it to shape the roadmap.
        </Typography>
      </Paper>

      <Grid container spacing={2}>
        {contactCards.map((card) => (
          <Grid item xs={12} md={4} key={card.title}>
            <Paper elevation={0} className={classes.card}>
              <Box mb={1}>{card.icon}</Box>
              <Typography variant="h6">{card.title}</Typography>
              <Typography color="textSecondary" style={{ marginTop: 8 }}>{card.text}</Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Paper elevation={0} className={classes.formPanel} style={{ marginTop: 16 }}>
        <Typography variant="h6" style={{ fontFamily: '"Manrope", sans-serif', fontWeight: 800 }}>
          Send us a message
        </Typography>
        <Typography color="textSecondary" style={{ marginTop: 4 }}>
          We typically review feedback within 1-2 business days.
        </Typography>
        {submitted && (
          <Box className={classes.success}>
            <Typography color="primary">Thanks. We received your message.</Typography>
          </Box>
        )}
        <form noValidate onSubmit={onSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Name"
                margin="normal"
                variant="outlined"
                value={form.name}
                onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))}
                error={Boolean(errors.name)}
                helperText={errors.name}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Email"
                margin="normal"
                variant="outlined"
                value={form.email}
                onChange={(event) => setForm((prev) => ({ ...prev, email: event.target.value }))}
                error={Boolean(errors.email)}
                helperText={errors.email}
              />
            </Grid>
          </Grid>
          <TextField
            fullWidth
            label="Message"
            margin="normal"
            variant="outlined"
            multiline
            rows={6}
            value={form.message}
            onChange={(event) => setForm((prev) => ({ ...prev, message: event.target.value }))}
            error={Boolean(errors.message)}
            helperText={errors.message}
          />
          <Box mt={2}>
            <Button type="submit" variant="contained" color="primary">Send Message</Button>
          </Box>
        </form>
      </Paper>
    </Container>
  );
};

export default ContactPage;
